import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { Skeleton } from '@/components/Skeleton'
import { useTheme } from '@/hooks/useTheme'

export interface PriceChangeIndicatorProps {
  percentageChange24h?: number
  revenue24hFiatAmount?: number
  showRevenue?: boolean
  skeletonActive?: boolean
}

export const PriceChangeIndicator: React.FC<PriceChangeIndicatorProps> = ({
  percentageChange24h = 0,
  revenue24hFiatAmount = 0,
  showRevenue = false,
  skeletonActive = false,
}) => {
  const theme = useTheme()

  const skeletonWidth = 60
  const skeletonHeight = 24

  if (skeletonActive) {
    return (
      <Skeleton
        viewBox={`0 0 ${skeletonWidth} ${skeletonHeight}`}
        style={{
          width: skeletonWidth,
          height: skeletonHeight,
          alignSelf: 'center',
        }}
      >
        <rect rx="12" ry="12" width={skeletonWidth} height={skeletonHeight} />
      </Skeleton>
    )
  }

  if (percentageChange24h === 0) return null

  const isProfit = percentageChange24h > 0
  const handledPercentage = Math.abs(percentageChange24h).toFixed(2)

  // Форматируем сумму дохода/убытка
  const formattedRevenue = revenue24hFiatAmount
    ? `$${Math.abs(revenue24hFiatAmount).toFixed(2)}`
    : null

  return (
    <BaseView
      data-component="price-change-indicator"
      row
      align="center"
      gap={6}
      px={6}
      py={2}
      rounded={20}
      style={{
        backgroundColor: isProfit
          ? theme.colors.background.profitFlag
          : theme.colors.background.loseFlag,
      }}
    >
      {/* Revenue amount (optional) */}
      {showRevenue &&
        formattedRevenue &&
        Number(revenue24hFiatAmount) !== 0 && (
          <BaseView row align="center" gap={2}>
            <BaseText
              fontSize={12}
              fontFamily="medium"
              color={isProfit ? 'label.flagProfit' : 'label.flagLosses'}
            >
              {isProfit ? '+' : '-'}
              {formattedRevenue}
            </BaseText>
          </BaseView>
        )}

      {/* Percentage change with arrow */}
      <BaseView row align="center">
        <AppIcons
          name={isProfit ? 'smallArrowUp' : 'smallArrowDown'}
          size={16}
          color={isProfit ? 'label.flagProfit' : 'theme.colors.label.amount'}
        />
        <BaseText
          fontSize={12}
          fontFamily="bold"
          color={isProfit ? 'label.flagProfit' : ''}
        >
          {handledPercentage}%
        </BaseText>
      </BaseView>
    </BaseView>
  )
}
